import React from 'react';
import { Link, Routes, Route, Navigate, useLocation } from 'react-router';
import { BenchmarksIndexPage, BenchmarkDetailPage } from './BenchmarksPage';
import { TermsSection, Section, CodeBlock } from './docs/shared';
import PrivacyPolicyPage from './docs/PrivacyPolicyPage';
import AcceptableUsePolicyPage from './docs/AcceptableUsePolicyPage';
import usePageMeta from './public/usePageMeta';


// ── Styles ───────────────────────────────────────────────────────────────────
const PAGE_BG = '#060d18';
const SIDEBAR_BG = '#0a1120';

const SECTIONS = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'benchmarks',      label: 'Model Benchmarks' },
  { id: 'terms',           label: 'Terms of Service' },
  { id: 'privacy',         label: 'Privacy Policy' },
  { id: 'acceptable-use',  label: 'Acceptable Use' },
];

// ── Sidebar ──────────────────────────────────────────────────────────────────
function DocsSidebar() {
  const location = useLocation();
  const current = location.pathname.replace('/docs', '').replace(/^\//, '').split('/')[0] || 'getting-started';

  return (
    <nav style={{
      width: 220, minWidth: 220, background: SIDEBAR_BG, borderRight: '1px solid #1e293b',
      padding: '24px 0', position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
    }}>
      <div style={{ padding: '0 20px 16px', color: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
        Documentation
      </div>
      {SECTIONS.map(s => {
        const active = current === s.id;
        return (
          <Link key={s.id} to={`/docs/${s.id}`} style={{
            display: 'block', padding: '10px 20px', fontSize: 14, textDecoration: 'none',
            color: active ? '#e2e8f0' : '#94a3b8',
            background: active ? '#1e293b' : 'transparent',
            borderLeft: active ? '3px solid #38bdf8' : '3px solid transparent',
            fontWeight: active ? 600 : 400,
            transition: 'all 0.12s',
          }}>
            {s.label}
          </Link>
        );
      })}
      <div style={{ padding: '24px 20px 0', borderTop: '1px solid #1e293b', marginTop: 16 }}>
        <Link to="/" style={{ color: '#64748b', fontSize: 12, textDecoration: 'none' }}>
          ← Back to Spectra
        </Link>
      </div>
    </nav>
  );
}

// ── Getting Started ──────────────────────────────────────────────────────────
function GettingStartedPage() {
  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ color: '#e2e8f0', fontSize: '1.8rem', fontWeight: 700, marginBottom: 8 }}>
        Getting Started
      </h1>
      <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.7, marginBottom: 32 }}>
        Spectra is an AI-driven materials discovery platform. Describe the material you need
        in plain language and the system will route your request to the best generative models,
        rank candidates by structural quality, and provide deep computational analysis.
      </p>

      <Section title="1. Describe your target material">
        <p>Navigate to the <strong>Search</strong> page and enter a natural-language description of the material you need. For example:</p>
        <CodeBlock>
          {`"High bulk modulus ceramic for thermal barrier coating"
"Novel lithium-ion battery cathode with low formation energy"
"Magnetic intermetallic with high Curie temperature"`}
        </CodeBlock>
        <p>You can also click a <strong>category chip</strong> on the landing page to pre-select a domain (e.g. Superconductors, Battery Materials).</p>
      </Section>

      <Section title="2. Review generated candidates">
        <p>
          Spectra routes your request to the optimal model variants, generates candidate structures,
          and ranks them by a confidence-weighted score. Results appear in the <strong>Results</strong> page
          with interactive 3D crystal viewers and per-candidate property cards.
        </p>
      </Section>

      <Section title="3. Run computational analyses">
        <p>
          Use the <strong style={{ color: '#4ade80' }}>Analyze ▾</strong> dropdown on any candidate to run structural analyses:
        </p>
        <ul style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.8, paddingLeft: 20 }}>
          <li><strong>Symmetry</strong> — space group, crystal system, Wyckoff sites</li>
          <li><strong>Voronoi</strong> — coordination environments per atom</li>
          <li><strong>Bond Distribution</strong> — per-pair bond statistics and histograms</li>
          <li><strong>DFT Surrogate</strong> — fast CHGNet prediction of formation energy, stability, forces, stress, magnetic character</li>
          <li><strong>Phase Stability</strong> — convex hull analysis against known Materials Project phases</li>
          <li><strong>Real DFT</strong> — full GPAW calculations: single point, relaxation, elastic constants, DOS, band structure</li>
        </ul>
      </Section>

      <Section title="4. Generate reports">
        <p>
          Use the <strong style={{ color: '#7c3aed' }}>Reports ▾</strong> dropdown to generate professional documents:
        </p>
        <ul style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.8, paddingLeft: 20 }}>
          <li><strong>Scientific Document</strong> — comprehensive structural and property summary</li>
          <li><strong>Economic Report</strong> — cost analysis, supply risk, recyclability, market positioning</li>
          <li><strong>Patent Packet</strong> — patent-grade writeup with prior art screening (PubChem + USPTO)</li>
        </ul>
      </Section>

      <Section title="5. Save and compare">
        <p>
          Click the ★ button on any candidate to save it. Saved candidates persist across sessions
          and appear on the <strong>Saved</strong> page. Use research notes to annotate candidates
          with observations for your team.
        </p>
      </Section>

      <Section title="Supported models">
        <p>Spectra currently integrates the following generative models. See <Link to="/docs/benchmarks" style={{ color: '#38bdf8', textDecoration: 'none' }}>Model Benchmarks</Link> for published performance metrics.</p>
        <ul style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.8, paddingLeft: 20 }}>
          <li><strong>MatterGen</strong> — diffusion-based inorganic crystal generation (Microsoft, Nature 2025)</li>
          <li><strong>IBM FM4M</strong> — foundation model for molecules: SMI-TED, BART, MHG variants. IBM archived the upstream repository on 2026-10-07; the published weights Spectra runs remain available under Apache-2.0, so these capabilities are unaffected.</li>
          <li><strong>GT4SD</strong>: property-conditioned molecular generation via the Regression Transformer. Give it a target logP from -5 to 10 and it edits a seed molecule toward it; every molecule comes back with its measured logP and the signed distance from your target. <em>Experimental</em>: targets above about 3.4 logP are accepted but rarely reached from a seed, and IBM's own model card lists production-level inference as out of scope. See Model Benchmarks for per-variant availability.</li>
        </ul>
      </Section>
    </div>
  );
}

// ── Terms of Service ─────────────────────────────────────────────────────────
function TermsOfServicePage() {
  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ color: '#e2e8f0', fontSize: '1.8rem', fontWeight: 700, marginBottom: 8 }}>
        Terms of Service
      </h1>
      <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        Last updated: March 20, 2026
      </p>
      <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.7, marginBottom: 32 }}>
        By creating an account or logging in to Spectra, you and any entity you represent agree to the following terms.
        Please review these terms carefully before using the platform.
      </p>

      <TermsSection num="1" title="Acceptance of Terms">
        By accessing or using the Spectra platform ("Service") operated by CrimsonScan ("Company", "we", "us"),
        you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of an
        organization, you represent that you have authority to bind that organization to these Terms.
        Logging into the Service constitutes acceptance of these Terms.
      </TermsSection>

      <TermsSection num="2" title="Description of Service">
        Spectra is an AI-driven materials discovery platform that provides access to generative models for
        crystal and molecular structure prediction, computational analysis tools (including DFT calculations),
        and report generation capabilities. The Service is provided on a subscription basis.
      </TermsSection>

      <TermsSection num="3" title="Accounts and Access">
        You must create an account to use the Service beyond publicly available pages (documentation, benchmarks).
        You are responsible for maintaining the confidentiality of your account credentials.
        Access is scoped to your organization; data generated through the Service belongs to your organization's
        workspace. You must not share credentials or allow unauthorized access to your account.
      </TermsSection>

      <TermsSection num="4" title="Subscriptions and Payment">
        The Service is available under paid subscription plans. Pricing, seat allocations, and billing cycles
        are as described at the time of purchase. Payments are processed through Stripe. You authorize
        recurring charges for your subscription plan. Cancellation takes effect at the end of the current
        billing period. Refunds are provided only as required by applicable law.
      </TermsSection>

      <TermsSection num="5" title="Intellectual Property">
        <strong>Your data:</strong> You retain ownership of all input data, generated candidates, analyses,
        reports, and research notes produced through your use of the Service. CrimsonScan does not claim
        ownership of your generated materials or analysis results.
        <br /><br />
        <strong>Pooled research data license:</strong> For plans that include pooled research data participation
        (see Section 9 of our Privacy Policy), you grant CrimsonScan a perpetual, irrevocable, worldwide,
        royalty-free license to retain and use anonymized candidate structures and associated computed
        properties, stripped of account, organization, user, and project identifiers, for the purposes
        described in that section. This license survives termination of your subscription. It does not
        extend to your queries, notes, reports, or uploaded files, and does not affect your ownership
        under "Your data" above.
        <br /><br />
        <strong>Our platform:</strong> The Service, including its software, algorithms, interfaces, and documentation,
        is the intellectual property of CrimsonScan. You are granted a limited, non-exclusive, non-transferable
        license to use the Service for the duration of your subscription.
        <br /><br />
        <strong>Underlying models:</strong> Generative models integrated into the Service (MatterGen, IBM FM4M, GT4SD)
        are subject to their respective open-source licenses. CrimsonScan does not claim ownership of these models.
      </TermsSection>

      <TermsSection num="6" title="Acceptable Use">
        You agree not to: (a) reverse-engineer, decompile, or disassemble the Service; (b) use the Service to
        develop competing products; (c) circumvent access controls, rate limits, or security measures;
        (d) upload malicious content or attempt to compromise the platform's infrastructure; (e) use the Service
        for any purpose that violates applicable law.
      </TermsSection>

      <TermsSection num="7" title="Computational Resources">
        DFT calculations and generative model runs consume computational resources. Resource usage is subject to
        fair-use limits based on your subscription tier. CrimsonScan reserves the right to queue, throttle, or
        decline jobs that exceed plan limits. Enterprise customers may connect their own compute infrastructure
        ("Bring Your Own Compute") under separate terms.
      </TermsSection>

      <TermsSection num="8" title="Disclaimer of Warranties">
        The Service is provided "as is" and "as available." CrimsonScan makes no warranties, express or implied,
        regarding the accuracy, reliability, or fitness for a particular purpose of any generated materials,
        predictions, analyses, or reports. Computational results (including DFT calculations, stability predictions,
        and property estimates) are provided for research and informational purposes. They do not constitute
        professional scientific advice and should be validated independently before any commercial or safety-critical
        application.
      </TermsSection>

      <TermsSection num="9" title="Limitation of Liability">
        To the maximum extent permitted by applicable law, CrimsonScan shall not be liable for any indirect,
        incidental, special, consequential, or punitive damages, including but not limited to loss of profits,
        data, or business opportunities, arising from your use of the Service. Our total liability shall not
        exceed the amount paid by you in the twelve (12) months preceding the claim.
      </TermsSection>

      <TermsSection num="10" title="Data Privacy">
        We collect and process data necessary to provide the Service, including account information, usage metrics,
        and generated content. Data is stored in secure cloud infrastructure (Google Cloud Platform) and is scoped
        to your organization. We do not sell your data to third parties. For detailed information, refer to our{' '}
        <Link to="/docs/privacy" style={{ color: '#38bdf8', textDecoration: 'none' }}>Privacy Policy</Link>.
      </TermsSection>

      <TermsSection num="11" title="Modifications to Terms">
        CrimsonScan may update these Terms at any time. We will notify you of material changes via email or
        in-platform notification at least 30 days before they take effect. Continued use of the Service after
        the effective date constitutes acceptance of the updated Terms.
      </TermsSection>

      <TermsSection num="12" title="Termination">
        Either party may terminate the agreement at any time. CrimsonScan may suspend or terminate your account
        for violation of these Terms. Upon termination, you may export your data for 30 days, after which it
        will be permanently deleted.
      </TermsSection>

      <TermsSection num="13" title="Governing Law">
        These Terms shall be governed by and construed in accordance with the laws of the State of Delaware,
        United States, without regard to its conflict of law provisions. Any disputes arising under these Terms
        shall be resolved in the state or federal courts located in Delaware.
      </TermsSection>

      <TermsSection num="14" title="Contact">
        For questions about these Terms, contact us at{' '}
        <span style={{ color: '#38bdf8' }}>legal@crimsonscan.com</span>.
      </TermsSection>
    </div>
  );
}

// Title, description and canonical per docs page (benchmark pages set their own).
const SECTION_META = {
  'getting-started': ['Getting Started with Spectra | CrimsonScan Docs', 'How to describe a target, run generation, rank candidates, validate with DFT and export reports in CrimsonScan Spectra.'],
  terms: ['Terms of Service | CrimsonScan', 'Terms of Service for CrimsonScan Spectra, the AI materials discovery platform.'],
  privacy: ['Privacy Policy | CrimsonScan', 'How CrimsonScan collects, uses and protects data in Spectra, including the pooled research dataset.'],
  'acceptable-use': ['Acceptable Use Policy | CrimsonScan', 'What CrimsonScan Spectra may and may not be used for.'],
};

function SectionMeta({ id }) {
  const [title, description] = SECTION_META[id];
  usePageMeta({ title, description, path: `/docs/${id}` });
  return null;
}

// ── Docs Layout ──────────────────────────────────────────────────────────────
export default function DocsPage() {
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: PAGE_BG }}>
      <DocsSidebar />
      <main style={{ flex: 1, padding: '40px 32px', overflowY: 'auto' }}>
        <Routes>
          <Route index element={<Navigate to="getting-started" replace />} />
          <Route path="getting-started" element={<><SectionMeta id="getting-started" /><GettingStartedPage /></>} />
          <Route path="benchmarks" element={<BenchmarksIndexPage />} />
          <Route path="benchmarks/:modelId/:variantId" element={<BenchmarkDetailPage />} />
          <Route path="terms" element={<><SectionMeta id="terms" /><TermsOfServicePage /></>} />
          <Route path="privacy" element={<><SectionMeta id="privacy" /><PrivacyPolicyPage /></>} />
          <Route path="acceptable-use" element={<><SectionMeta id="acceptable-use" /><AcceptableUsePolicyPage /></>} />

          <Route path="*" element={<Navigate to="getting-started" replace />} />
        </Routes>
      </main>
    </div>
  );
}
